// App — thin platform root + page router (platform-isolation, #1657).
// All chat-shell state/logic lives in hooks/useChatShell (SHARED); the chat JSX lives
// in a per-platform shell FORK (mobile/MobileShell or desktop/DesktopShell), selected
// by useIsDesktop(). App keeps ONLY the auth/loading/page-route early-returns, then
// renders the platform shell. Both shells consume the identical useChatShell() output.
import { lazy, Suspense } from 'preact/compat';
import { useEffect } from 'preact/hooks';
import { useChatShell } from '../hooks/useChatShell';
import { useIsDesktop } from '../shared-ui/useIsDesktop';
import { MobileShell } from '../mobile/MobileShell';
import { BalancePill } from '../shared-ui/BalancePill';
import { TopUpModal } from '../shared-ui/TopUpModal';
import { LowBalanceNotice } from '../shared-ui/LowBalanceNotice';
import { TopupSuccessNotice } from '../shared-ui/TopupSuccessNotice';
import { useCreditsStore, TOPUP_MARKER_TTL_MS } from '../state/creditsStore';
import { getCreditsBalance } from '../api/billing';
import { AuthPage } from '../auth/AuthPage';
import { GuidePage } from './GuidePage';
import { AdminPage } from './AdminPage';
import { ArchPage } from './ArchPage';
import { HarnessPage } from './HarnessPage';
import { MemosPage } from './MemosPage';
import { LessonsPage } from './LessonsPage';
import { MonitorPage } from './MonitorPage';
import { WorkflowsPage } from './WorkflowsPage';
import { PortalPage } from './PortalPage';
import { isNextShellEnabled } from '../next/shellFlag';

// Desktop fork (#1657 Phase 3) — lazy so the desktop shell + its CSS are code-split
// out of the mobile bundle and never ship to mobile clients (desktop/README contract).
const DesktopShell = lazy(() =>
  import('../desktop/DesktopShell').then((m) => ({ default: m.DesktopShell })),
);

// Voice-first shell (plan ui-modern-css-framework-va-accounting-2026-10-21 §5.1): ONE shell for both viewports, behind a
// flag — `?ui=next` or localStorage `va-ui-next=1` (next/shellFlag.ts). Lazy so it never ships to anyone who does not ask for
// it. Decided once per page load; the default path below is untouched.
const NEXT_SHELL = isNextShellEnabled();
const NextShell = lazy(() => import('../next/NextShell').then((m) => ({ default: m.NextShell })));

/** Topup-confirmation backstop poll (E2 tail b, PR B). Interval + bound: the webhook
 *  can lag the redirect by seconds, so we keep polling until a topup-since fact fires
 *  (or the bound / TTL is reached). NOT stopped on the first 200 (which may predate the
 *  credit). ~3s × 40 ≈ 2 min covers realistic webhook lag; the marker's 30-min TTL is the
 *  outer bound (the live socket reason:'topup' path can still fire later within it). */
const TOPUP_POLL_INTERVAL_MS = 3000;
const TOPUP_POLL_MAX_ATTEMPTS = 40;

export function App() {
  const shell = useChatShell();
  const isDesktop = useIsDesktop();
  const { authenticated, loading, memosEnabled, profileLoaded, lessonGeneratorEnabled, billingEnabled } = shell;

  // ── Topup-confirmation poll-on-return (E2 tail b, PR B) ─────────────────────
  // The common ordering is webhook-beats-return: the live reason:'topup' emit is often
  // dropped by the post-redirect socket-replay hole (useSocket ~L997), so the REST
  // since-poll is the real resolution path. Gated on a persisted pending-topup marker
  // AND activeUserId being SET (R-4a — so onTopupSinceFact routes into the bound wallet,
  // never the pre-bind buffer). Unconditional hook (before every early return) so it
  // runs on whatever route the edge checkout redirects back to.
  const activeUserId = useCreditsStore((s) => s.activeUserId);
  const pendingTopup = useCreditsStore((s) => s.pendingTopup);
  const onTopupSinceFact = useCreditsStore((s) => s.onTopupSinceFact);
  useEffect(() => {
    if (!pendingTopup || !activeUserId || pendingTopup.userId !== activeUserId) return;
    if (Date.now() - pendingTopup.ts >= TOPUP_MARKER_TTL_MS) {
      // Stale on arrival — clear it (onTopupSinceFact drops a TTL-lapsed marker).
      onTopupSinceFact({ userId: activeUserId, sinceCenti: 0 });
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      // Re-read the marker each tick: a fast-path socket topup may have consumed it.
      const cur = useCreditsStore.getState().pendingTopup;
      if (!cur || cur.userId !== activeUserId) return; // fired/cleared elsewhere → stop
      if (Date.now() - cur.ts >= TOPUP_MARKER_TTL_MS) {
        onTopupSinceFact({ userId: activeUserId, sinceCenti: 0 }); // clears stale
        return;
      }
      try {
        const view = await getCreditsBalance({ since: cur.ts });
        if (cancelled) return;
        const sinceCenti = view.topupSinceCenti ?? 0;
        onTopupSinceFact({ userId: activeUserId, sinceCenti });
        if (sinceCenti > 0) return; // confirmed — done
      } catch {
        // transient network/backoff — keep trying within the bound
      }
      attempts += 1;
      if (attempts >= TOPUP_POLL_MAX_ATTEMPTS) return;
      setTimeout(poll, TOPUP_POLL_INTERVAL_MS);
    };
    void poll();
    return () => {
      cancelled = true;
    };
  }, [pendingTopup, activeUserId, onTopupSinceFact]);

  // Auth page — GSI sign-in
  if (window.location.pathname.startsWith('/auth')) {
    if (authenticated) {
      window.location.href = '/';
      return null;
    }
    return <AuthPage />;
  }

  if (loading) {
    return (
      <div class="loading-screen">
        <div class="spinner" />
      </div>
    );
  }

  if (!authenticated) return null;

  // Auth-protected pages — render after auth check
  if (window.location.pathname === '/guide') return <GuidePage />;
  if (window.location.pathname === '/memos') {
    if (!memosEnabled) { window.location.replace('/'); return null; }
    return <MemosPage />;
  }
  if (window.location.pathname === '/lessons' || window.location.pathname.startsWith('/lessons/')) {
    if (!profileLoaded) return null; // wait for profile before deciding to redirect
    if (!lessonGeneratorEnabled) { window.location.replace('/'); return null; }
    const lessonId = window.location.pathname.startsWith('/lessons/')
      ? window.location.pathname.slice('/lessons/'.length) || undefined
      : undefined;
    return <LessonsPage lessonId={lessonId} />;
  }
  if (window.location.pathname === '/portal') {
    if (!profileLoaded) return null;
    if (!billingEnabled) { window.location.replace('/'); return null; }
    // TopUpModal MUST be mounted here too (E5 fix) — it is normally mounted once above
    // both shells (see the credits-surface block below), but /portal returns BEFORE that
    // block runs. PortalBalanceTab's "Top up" button drives the SAME creditsStore region
    // via openTopUpModal(null); without a mounted <TopUpModal/>, that store flip renders
    // nothing and the button is silently inert. Caught by e2e/portal/portal-tabs.spec.ts
    // "Top up" opens the modal (lane E5) — RED against the live deployment before this fix.
    return (
      <>
        <PortalPage />
        <TopUpModal />
        <TopupSuccessNotice />
      </>
    );
  }
  if (window.location.pathname === '/admin') return <AdminPage />;
  if (window.location.pathname === '/arch') return <ArchPage />;
  if (window.location.pathname === '/harness') return <HarnessPage />;
  if (window.location.pathname === '/monitor') return <MonitorPage />;
  if (window.location.pathname === '/workflows') return <WorkflowsPage />;

  // The flag renders the voice-first shell INSTEAD of the credits surface and both classic shells below (it draws the wallet
  // and the credits gate itself, in tokens). Not set: everything below is exactly as it was.
  if (NEXT_SHELL) {
    return (
      <Suspense fallback={<div class="loading-screen"><div class="spinner" /></div>}>
        <NextShell {...shell} />
      </Suspense>
    );
  }

  return (
    <>
      {/* Credits surface (F3) — mounted ONCE above both shells. Each renders NOTHING
          until the credits store leaves 'uninitialized' (dark by default), so a
          dogfood user whose tier never emits a balance sees zero change. */}
      <BalancePill />
      <TopUpModal />
      <LowBalanceNotice />
      <TopupSuccessNotice />
      {isDesktop ? (
        <Suspense fallback={<div class="loading-screen"><div class="spinner" /></div>}>
          <DesktopShell {...shell} />
        </Suspense>
      ) : (
        <MobileShell {...shell} />
      )}
    </>
  );
}
